interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR' | 'SUPPORT';
  permissions: AdminPermission[];
  isActive: boolean;
  lastLogin?: Date;
  createdAt: Date;
}

type AdminPermission =
  | 'USER_MANAGEMENT'
  | 'PROVIDER_VERIFICATION'
  | 'CONTENT_MODERATION'
  | 'DISPUTE_RESOLUTION'
  | 'PLATFORM_ANALYTICS'
  | 'SYSTEM_SETTINGS'
  | 'FINANCIAL_REPORTS'
  | 'SECURITY_MANAGEMENT';

interface ProviderVerificationRequest {
  id: string;
  providerId: string;
  providerName: string;
  businessName: string;
  email: string;
  phone: string;
  documents: {
    type: 'ID' | 'BUSINESS_LICENSE' | 'INSURANCE' | 'CERTIFICATION' | 'TAX_DOCUMENT';
    url: string;
    uploadedAt: Date;
    verified: boolean;
  }[];
  status: 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'REQUIRES_INFO';
  reviewedBy?: string;
  reviewNotes?: string;
  submittedAt: Date;
  reviewedAt?: Date;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface DisputeCase {
  id: string;
  jobId: string;
  customerId: string;
  providerId: string;
  type: 'QUALITY' | 'PAYMENT' | 'CANCELLATION' | 'COMMUNICATION' | 'SAFETY' | 'OTHER';
  status: 'OPEN' | 'INVESTIGATING' | 'MEDIATION' | 'RESOLVED' | 'ESCALATED' | 'CLOSED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  description: string;
  evidence: {
    type: 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'CHAT_LOG';
    content: string;
    uploadedBy: 'CUSTOMER' | 'PROVIDER' | 'ADMIN';
    uploadedAt: Date;
  }[];
  timeline: {
    action: string;
    performedBy: string;
    timestamp: Date;
    notes?: string;
  }[];
  assignedTo?: string;
  resolution?: {
    outcome: 'CUSTOMER_FAVOR' | 'PROVIDER_FAVOR' | 'MUTUAL_AGREEMENT' | 'NO_FAULT';
    compensation?: number;
    actions: string[];
    notes: string;
  };
  createdAt: Date;
  resolvedAt?: Date;
}

interface PlatformAnalytics {
  period: 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
  startDate: Date;
  endDate: Date;
  metrics: {
    totalUsers: number;
    activeUsers: number;
    newRegistrations: number;
    totalProviders: number;
    verifiedProviders: number;
    totalJobs: number;
    completedJobs: number;
    totalRevenue: number;
    platformFees: number;
    averageJobValue: number;
    customerSatisfaction: number;
    providerSatisfaction: number;
    disputeRate: number;
    cancellationRate: number;
  };
  trends: {
    userGrowth: number;
    revenueGrowth: number;
    jobGrowth: number;
    satisfactionTrend: number;
  };
  topCategories: {
    category: string;
    jobCount: number;
    revenue: number;
  }[];
  topLocations: {
    location: string;
    userCount: number;
    jobCount: number;
  }[];
}

interface ContentModerationItem {
  id: string;
  type: 'REVIEW' | 'JOB_DESCRIPTION' | 'PROVIDER_PROFILE' | 'MESSAGE' | 'IMAGE';
  contentId: string;
  content: string;
  reportedBy?: string;
  reportReason?: string;
  flaggedBy: 'USER_REPORT' | 'AUTO_DETECTION' | 'ADMIN_REVIEW';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REQUIRES_EDIT';
  moderatedBy?: string;
  moderationNotes?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  createdAt: Date;
  moderatedAt?: Date;
}

interface SystemAlert {
  id: string;
  type: 'SECURITY' | 'PERFORMANCE' | 'ERROR' | 'MAINTENANCE' | 'BUSINESS';
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  title: string;
  message: string;
  source: string;
  data?: any;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  resolved: boolean;
  resolvedAt?: Date;
  createdAt: Date;
}

class AdminService {
  private readonly STORAGE_KEY = 'admin';

  // Platform Analytics
  async getPlatformAnalytics(
    period: 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR',
    startDate?: Date,
    endDate?: Date
  ): Promise<PlatformAnalytics> {
    const analytics = this.generatePlatformAnalytics(period, startDate, endDate);
    return analytics;
  }

  private generatePlatformAnalytics(
    period: 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR',
    startDate?: Date,
    endDate?: Date
  ): PlatformAnalytics {
    const now = new Date();
    const start = startDate || this.getPeriodStart(period, now);
    const end = endDate || now;

    // Mock analytics data - in real app, this would come from database
    return {
      period,
      startDate: start,
      endDate: end,
      metrics: {
        totalUsers: 15420,
        activeUsers: 8930,
        newRegistrations: 234,
        totalProviders: 2840,
        verifiedProviders: 2156,
        totalJobs: 12450,
        completedJobs: 11230,
        totalRevenue: 2450000,
        platformFees: 245000,
        averageJobValue: 218,
        customerSatisfaction: 4.6,
        providerSatisfaction: 4.4,
        disputeRate: 2.3,
        cancellationRate: 5.8
      },
      trends: {
        userGrowth: 12.5,
        revenueGrowth: 18.3,
        jobGrowth: 15.7,
        satisfactionTrend: 2.1
      },
      topCategories: [
        { category: 'Plumbing', jobCount: 3420, revenue: 684000 },
        { category: 'Electrical', jobCount: 2890, revenue: 578000 },
        { category: 'Cleaning', jobCount: 2340, revenue: 468000 },
        { category: 'HVAC', jobCount: 1890, revenue: 567000 },
        { category: 'Gardening', jobCount: 1910, revenue: 382000 }
      ],
      topLocations: [
        { location: 'Dublin', userCount: 6780, jobCount: 5420 },
        { location: 'Cork', userCount: 2340, jobCount: 2890 },
        { location: 'Galway', userCount: 1890, jobCount: 1560 },
        { location: 'Waterford', userCount: 1230, jobCount: 980 },
        { location: 'Limerick', userCount: 1180, jobCount: 1100 }
      ]
    };
  }

  // Provider Verification Management
  async getVerificationRequests(
    status?: 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED',
    priority?: 'LOW' | 'MEDIUM' | 'HIGH'
  ): Promise<ProviderVerificationRequest[]> {
    let requests = this.getMockVerificationRequests();

    if (status) {
      requests = requests.filter(r => r.status === status);
    }

    if (priority) {
      requests = requests.filter(r => r.priority === priority);
    }

    return requests.sort((a, b) => {
      // Sort by priority first, then by submission date
      const priorityOrder = { 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;

      return b.submittedAt.getTime() - a.submittedAt.getTime();
    });
  }

  async reviewVerificationRequest(
    requestId: string,
    adminId: string,
    decision: 'APPROVE' | 'REJECT' | 'REQUIRE_INFO',
    notes: string
  ): Promise<void> {
    const requests = await this.getVerificationRequests();
    const request = requests.find(r => r.id === requestId);

    if (!request) {
      throw new Error('Verification request not found');
    }

    request.status = decision === 'APPROVE' ? 'APPROVED' :
      decision === 'REJECT' ? 'REJECTED' : 'REQUIRES_INFO';
    request.reviewedBy = adminId;
    request.reviewNotes = notes;
    request.reviewedAt = new Date();

    // In real app, this would update the database and notify the provider
    await this.saveVerificationRequests(requests);

    // Send notification to provider
    await this.notifyProviderVerificationResult(request);
  }

  // Dispute Resolution System
  async getDisputes(
    status?: DisputeCase['status'],
    priority?: DisputeCase['priority'],
    assignedTo?: string
  ): Promise<DisputeCase[]> {
    let disputes = this.getMockDisputes();

    if (status) {
      disputes = disputes.filter(d => d.status === status);
    }

    if (priority) {
      disputes = disputes.filter(d => d.priority === priority);
    }

    if (assignedTo) {
      disputes = disputes.filter(d => d.assignedTo === assignedTo);
    }

    return disputes.sort((a, b) => {
      // Sort by priority and creation date
      const priorityOrder = { 'URGENT': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;

      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }

  async assignDispute(disputeId: string, adminId: string): Promise<void> {
    const disputes = await this.getDisputes();
    const dispute = disputes.find(d => d.id === disputeId);

    if (!dispute) {
      throw new Error('Dispute not found');
    }

    dispute.assignedTo = adminId;
    dispute.status = 'INVESTIGATING';
    dispute.timeline.push({
      action: 'Dispute assigned to admin',
      performedBy: adminId,
      timestamp: new Date(),
      notes: `Dispute assigned for investigation`
    });

    this.saveDisputes(disputes);
  }

  async resolveDispute(
    disputeId: string,
    adminId: string,
    resolution: DisputeCase['resolution']
  ): Promise<void> {
    const disputes = await this.getDisputes();
    const dispute = disputes.find(d => d.id === disputeId);

    if (!dispute) {
      throw new Error('Dispute not found');
    }

    dispute.status = 'RESOLVED';
    if (resolution) {
      dispute.resolution = resolution;
      dispute.timeline.push({
        action: 'Dispute resolved',
        performedBy: adminId,
        timestamp: new Date(),
        notes: resolution.notes
      });
    }

    this.saveDisputes(disputes);

    // Notify involved parties
    await this.notifyDisputeResolution(dispute);
  }

  // Content Moderation
  async getModerationQueue(
    type?: ContentModerationItem['type'],
    severity?: ContentModerationItem['severity']
  ): Promise<ContentModerationItem[]> {
    let items = this.getMockModerationItems();

    if (type) {
      items = items.filter(i => i.type === type);
    }

    if (severity) {
      items = items.filter(i => i.severity === severity);
    }

    return items
      .filter(i => i.status === 'PENDING')
      .sort((a, b) => {
        const severityOrder = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
        if (severityDiff !== 0) return severityDiff;

        return b.createdAt.getTime() - a.createdAt.getTime();
      });
  }

  async moderateContent(
    itemId: string,
    adminId: string,
    decision: 'APPROVE' | 'REJECT' | 'REQUIRE_EDIT',
    notes: string
  ): Promise<void> {
    const items = this.getMockModerationItems();
    const item = items.find(i => i.id === itemId);

    if (!item) {
      throw new Error('Moderation item not found');
    }

    item.status = decision === 'APPROVE' ? 'APPROVED' :
      decision === 'REJECT' ? 'REJECTED' : 'REQUIRES_EDIT';
    item.moderatedBy = adminId;
    item.moderationNotes = notes;
    item.moderatedAt = new Date();

    this.saveModerationItems(items);

    // Take action based on decision
    if (decision === 'REJECT') {
      await this.takeContentAction(item);
    }
  }

  // System Monitoring
  async getSystemAlerts(
    severity?: SystemAlert['severity'],
    acknowledged?: boolean
  ): Promise<SystemAlert[]> {
    let alerts = this.getMockSystemAlerts();

    if (severity) {
      alerts = alerts.filter(a => a.severity === severity);
    }

    if (acknowledged !== undefined) {
      alerts = alerts.filter(a => a.acknowledged === acknowledged);
    }

    return alerts.sort((a, b) => {
      // Sort by severity and creation date
      const severityOrder = { 'CRITICAL': 4, 'ERROR': 3, 'WARNING': 2, 'INFO': 1 };
      const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
      if (severityDiff !== 0) return severityDiff;

      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }

  async acknowledgeAlert(alertId: string, adminId: string): Promise<void> {
    const alerts = this.getMockSystemAlerts();
    const alert = alerts.find(a => a.id === alertId);

    if (!alert) {
      throw new Error('Alert not found');
    }

    alert.acknowledged = true;
    alert.acknowledgedBy = adminId;
    alert.acknowledgedAt = new Date();

    this.saveSystemAlerts(alerts);
  }

  async resolveAlert(alertId: string, adminId: string): Promise<void> {
    const alerts = this.getMockSystemAlerts();
    const alert = alerts.find(a => a.id === alertId);

    if (!alert) {
      throw new Error('Alert not found');
    }

    alert.resolved = true;
    alert.resolvedAt = new Date();

    this.saveSystemAlerts(alerts);
  }

  // User Management
  async suspendUser(
    userId: string,
    adminId: string,
    reason: string,
    duration?: number // days
  ): Promise<void> {
    // In real app, this would update user status in database
    const suspension = {
      userId,
      reason,
      suspendedBy: adminId,
      suspendedAt: new Date(),
      expiresAt: duration ? new Date(Date.now() + duration * 24 * 60 * 60 * 1000) : undefined
    };

    this.saveUserSuspension(suspension);

    // Notify user
    await this.notifyUserSuspension(userId, suspension);
  }

  async unsuspendUser(userId: string, adminId: string): Promise<void> {
    // Remove suspension
    this.removeUserSuspension(userId);

    // Notify user
    await this.notifyUserUnsuspension(userId);
  }

  // Financial Reports
  async getFinancialReport(
    period: 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR',
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    totalRevenue: number;
    platformFees: number;
    providerPayouts: number;
    refunds: number;
    chargebacks: number;
    netRevenue: number;
    transactionCount: number;
    averageTransactionValue: number;
    topProviders: { id: string; name: string; revenue: number }[];
    revenueByCategory: { category: string; revenue: number }[];
  }> {
    // Mock financial data
    return {
      totalRevenue: 2450000,
      platformFees: 245000,
      providerPayouts: 2205000,
      refunds: 12300,
      chargebacks: 3400,
      netRevenue: 229300,
      transactionCount: 11230,
      averageTransactionValue: 218,
      topProviders: [
        { id: 'prov1', name: 'Elite Plumbing', revenue: 45000 },
        { id: 'prov2', name: 'TechFix Solutions', revenue: 38000 },
        { id: 'prov3', name: 'Clean Masters', revenue: 32000 }
      ],
      revenueByCategory: [
        { category: 'Plumbing', revenue: 684000 },
        { category: 'Electrical', revenue: 578000 },
        { category: 'HVAC', revenue: 567000 },
        { category: 'Cleaning', revenue: 468000 }
      ]
    };
  }

  // Helper Methods
  private getPeriodStart(period: string, date: Date): Date {
    const start = new Date(date);

    switch (period) {
      case 'DAY':
        start.setHours(0, 0, 0, 0);
        break;
      case 'WEEK':
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        break;
      case 'MONTH':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'QUARTER':
        const quarter = Math.floor(start.getMonth() / 3);
        start.setMonth(quarter * 3, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'YEAR':
        start.setMonth(0, 1);
        start.setHours(0, 0, 0, 0);
        break;
    }

    return start;
  }

  // Mock Data Generators
  private getMockVerificationRequests(): ProviderVerificationRequest[] {
    return [
      {
        id: 'ver_1',
        providerId: 'prov_1',
        providerName: 'John Smith',
        businessName: 'Smith Plumbing Services',
        email: 'john@smithplumbing.ie',
        phone: '+353 1 234 5678',
        documents: [
          { type: 'ID', url: '/docs/id_1.pdf', uploadedAt: new Date(), verified: false },
          { type: 'BUSINESS_LICENSE', url: '/docs/license_1.pdf', uploadedAt: new Date(), verified: false },
          { type: 'INSURANCE', url: '/docs/insurance_1.pdf', uploadedAt: new Date(), verified: false }
        ],
        status: 'PENDING',
        submittedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        priority: 'HIGH'
      }
      // Add more mock requests...
    ];
  }

  private getMockDisputes(): DisputeCase[] {
    return [
      {
        id: 'dispute_1',
        jobId: 'job_1',
        customerId: 'cust_1',
        providerId: 'prov_1',
        type: 'QUALITY',
        status: 'OPEN',
        priority: 'HIGH',
        description: 'Work was not completed to satisfaction',
        evidence: [
          {
            type: 'TEXT',
            content: 'The plumbing work was not done properly',
            uploadedBy: 'CUSTOMER',
            uploadedAt: new Date()
          }
        ],
        timeline: [
          {
            action: 'Dispute created',
            performedBy: 'cust_1',
            timestamp: new Date(),
            notes: 'Customer initiated dispute'
          }
        ],
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
      }
      // Add more mock disputes...
    ];
  }

  private getMockModerationItems(): ContentModerationItem[] {
    return [
      {
        id: 'mod_1',
        type: 'REVIEW',
        contentId: 'review_1',
        content: 'This provider was terrible and unprofessional',
        reportedBy: 'prov_1',
        reportReason: 'Inappropriate language',
        flaggedBy: 'USER_REPORT',
        status: 'PENDING',
        severity: 'MEDIUM',
        createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000)
      }
      // Add more mock items...
    ];
  }

  private getMockSystemAlerts(): SystemAlert[] {
    return [
      {
        id: 'alert_1',
        type: 'PERFORMANCE',
        severity: 'WARNING',
        title: 'High Response Time',
        message: 'API response time has increased to 2.5s average',
        source: 'monitoring_system',
        acknowledged: false,
        resolved: false,
        createdAt: new Date(Date.now() - 30 * 60 * 1000)
      }
      // Add more mock alerts...
    ];
  }

  // Storage Methods (mock implementations)
  private saveVerificationRequests(requests: ProviderVerificationRequest[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_verification`, JSON.stringify(requests));
  }

  private saveDisputes(disputes: DisputeCase[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_disputes`, JSON.stringify(disputes));
  }

  private saveModerationItems(items: ContentModerationItem[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_moderation`, JSON.stringify(items));
  }

  private saveSystemAlerts(alerts: SystemAlert[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_alerts`, JSON.stringify(alerts));
  }

  private saveUserSuspension(suspension: any): void {
    const suspensions = JSON.parse(localStorage.getItem(`${this.STORAGE_KEY}_suspensions`) || '[]');
    suspensions.push(suspension);
    localStorage.setItem(`${this.STORAGE_KEY}_suspensions`, JSON.stringify(suspensions));
  }

  private removeUserSuspension(userId: string): void {
    const suspensions = JSON.parse(localStorage.getItem(`${this.STORAGE_KEY}_suspensions`) || '[]');
    const filtered = suspensions.filter((s: any) => s.userId !== userId);
    localStorage.setItem(`${this.STORAGE_KEY}_suspensions`, JSON.stringify(filtered));
  }

  // Notification Methods (mock implementations)
  private async notifyProviderVerificationResult(request: ProviderVerificationRequest): Promise<void> {
    console.log(`Notifying provider ${request.providerId} of verification result: ${request.status}`);
  }

  private async notifyDisputeResolution(dispute: DisputeCase): Promise<void> {
    console.log(`Notifying parties of dispute resolution: ${dispute.id}`);
  }

  private async takeContentAction(item: ContentModerationItem): Promise<void> {
    console.log(`Taking action on rejected content: ${item.id}`);
  }

  private async notifyUserSuspension(userId: string, suspension: any): Promise<void> {
    console.log(`Notifying user ${userId} of suspension`);
  }

  private async notifyUserUnsuspension(userId: string): Promise<void> {
    console.log(`Notifying user ${userId} of unsuspension`);
  }
}

export const adminService = new AdminService();
export type {
  AdminUser,
  AdminPermission,
  ProviderVerificationRequest,
  DisputeCase,
  PlatformAnalytics,
  ContentModerationItem,
  SystemAlert
};